import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Box,
  Button,
  Typography
} from "@mui/material";
import { useState } from "react";
import { canChangeTeam, getTeam } from "../../../../../utils/escaladeUtils";
import AddEscaladeModal from "./AddEscaladeModal";
import ChangeTeamModal from "./ChangeTeamModal";
import CloseEscaladeModal from "./CloseEscaladeModal";
import HistoryCard from "./HistoryCard";
import { formatDate} from '../../../../../utils/escaladeUtils';
import AddIcon from '@mui/icons-material/Add';
import Chip from '@mui/material/Chip';
import NorthEastIcon from '@mui/icons-material/NorthEast';




// ─── EscaladeTab (export default) ────────────────────────────────
/**
 * Props:
 *   ticketId             {number}
 *   internalStatus       {array}
 *   activeInternalStatus {object|null}
 *   isEscaladed          {boolean}
 *   onRefresh            {fn}
 *   enCours              {boolean}
 */
export default function EscaladeTab({
  ticketId,
  internalStatus = [],
  activeInternalStatus,
  isEscaladed,
  onRefresh,
  enCours,
}) {
  const [openAdd,    setOpenAdd]    = useState(false);
  const [openChange, setOpenChange] = useState(false);
  const [openClose,  setOpenClose]  = useState(false);

  const activeTeam   = activeInternalStatus ? getTeam(activeInternalStatus.type) : null;
  // Règle métier : "Changer d'équipe" disponible uniquement si Dev (type 2)
  const showChange   = isEscaladed && canChangeTeam(activeInternalStatus?.type);
  const history      = internalStatus.filter(s => s.dateFin !== null && s.dateFin !== undefined);

  const handleSuccess = () => onRefresh();

  return (
    <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>

      {/* ── Pas d'escalade + ticket en cours ── */}
      {!isEscaladed && enCours && (
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          py: 6, gap: 2, border: '1.5px dashed #AFA9EC', borderRadius: '12px', backgroundColor: '#FAFAFA',
        }}>
          <NorthEastIcon sx={{ fontSize: 40, color: '#AFA9EC' }} />
          <Typography sx={{ fontSize: 14, color: '#888', textAlign: 'center' }}>
            Aucune escalade active sur ce ticket.
          </Typography>
          <Button onClick={() => setOpenAdd(true)} startIcon={<AddIcon />} variant="contained"
            sx={{ textTransform: 'none', borderRadius: '20px', backgroundColor: '#2A66C6', '&:hover': { backgroundColor: '#3C3489' }, px: 3 }}>
            Créer une escalade
          </Button>
        </Box>
      )}

      {/* ── Pas d'escalade + ticket non en cours ── */}
      {!isEscaladed && !enCours && internalStatus.length === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 1 }}>
          <CheckCircleIcon sx={{ fontSize: 40, color: '#C0DD97' }} />
          <Typography sx={{ fontSize: 14, color: '#888' }}>Aucune escalade sur ce ticket.</Typography>
        </Box>
      )}

      {/* ── Escalade active ── */}
      {isEscaladed && activeInternalStatus && activeTeam && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
            Escalade active
          </Typography>

          <Box sx={{ p: 2, borderRadius: '12px', backgroundColor: activeTeam.bg, border: `2px solid ${activeTeam.border}` }}>

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: '8px', backgroundColor: '#fff', border: `1px solid ${activeTeam.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <NorthEastIcon sx={{ fontSize: 16, color: activeTeam.color }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: activeTeam.color }}>
                    {activeTeam.label}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#888' }}>
                    Depuis {formatDate(activeInternalStatus.dateDebut)}
                  </Typography>
                </Box>
              </Box>
              <Chip label="En cours" size="small"
                sx={{ backgroundColor: '#fff', color: activeTeam.color, border: `1.5px solid ${activeTeam.color}`, fontWeight: 600, fontSize: 11 }} />
            </Box>

            {/* Num ticket Dev */}
            {activeInternalStatus.numTicketDev && (
              <Typography sx={{ fontSize: 12, color: '#185FA5', mb: 0.75, fontWeight: 500 }}>
                Ticket Dev : #{activeInternalStatus.numTicketDev}
              </Typography>
            )}

            {/* Description */}
            {activeInternalStatus.description && (
              <Typography sx={{ fontSize: 13, color: '#444', mb: 1.5, lineHeight: 1.5 }}>
                {activeInternalStatus.description}
              </Typography>
            )}

            {/* Actions dans l'onglet */}
            {enCours && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pt: 1, borderTop: `1px solid ${activeTeam.border}` }}>
                {/* Changer d'équipe — Dev uniquement */}
                {showChange && (
                  <Button onClick={() => setOpenChange(true)} startIcon={<SwapHorizIcon sx={{ fontSize: 15 }} />}
                    size="small" variant="outlined"
                    sx={{ textTransform: 'none', borderRadius: '20px', fontSize: 12, color: activeTeam.color, borderColor: activeTeam.color, '&:hover': { backgroundColor: '#fff' } }}>
                    Changer d'équipe
                  </Button>
                )}
                <Button onClick={() => setOpenClose(true)} startIcon={<CheckCircleIcon sx={{ fontSize: 15 }} />}
                  size="small" variant="contained"
                  sx={{ textTransform: 'none', borderRadius: '20px', fontSize: 12, backgroundColor: '#3B6D11', '&:hover': { backgroundColor: '#27500A' } }}>
                  Clôturer l'escalade
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* ── Historique ── */}
      {history.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
            Historique ({history.length})
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[...history].reverse().map(item => <HistoryCard key={item.id} item={item} />)}
          </Box>
        </Box>
      )}

      {/* ── Modals ── */}
      <AddEscaladeModal    open={openAdd}    onClose={() => setOpenAdd(false)}    ticketId={ticketId} onSuccess={handleSuccess} />
      <ChangeTeamModal   open={openChange} onClose={() => setOpenChange(false)} activeInternalStatus={activeInternalStatus} ticketId={ticketId} onSuccess={handleSuccess} />
      <CloseEscaladeModal  open={openClose}  onClose={() => setOpenClose(false)}  activeInternalStatus={activeInternalStatus} ticketId={ticketId} onSuccess={handleSuccess} />
    </Box>
  );
}